import type { IEvent } from "../../interfaces/event";
import type { IResource } from "../../interfaces/resource";
import { IRsoConstraintLevels } from "../../interfaces/rsoConfiguration";
import { logAIEmpowerMappedResources } from "./fetchAndLogAIEmpowerMappedResources";
import { logAIEmpowerUnassignedJobs } from "./fetchAndLogAIEmpowerUnassignedJobs";
import { logAIEmpowerBookings } from "./fetchAndLogAIEmpowerBookings";

export interface PlanningWindow {
  start: string;
  end: string;
}

export interface AIEmpowerPayload {
  resources: any[];
  bookings: any[];
  unassignedJobs: any[];
  planningWindow: PlanningWindow;
  siteBaseLocation?: {
    latitude: number | null;
    longitude: number | null;
  };
  rsoConstraintLevels: IRsoConstraintLevels;
  // Instruct downstream optimizers/LLMs to always include a human-readable reason
  // for each suggested allocation, referencing which comparisons were used.
  explanation: {
    includeReasonPerAllocation: true;
    comparisonCriteria: string[];
    guidance: string;
  };
}

export interface BuildArgs {
  resources: IResource[];
  shifts: IEvent[];
  planningWindow: PlanningWindow;
  siteBaseLocation?: {
    latitude: number | null;
    longitude: number | null;
  };
  rsoConstraintLevels: IRsoConstraintLevels;
}

export class AIEmpowerPayloadBuilder {
  static build(args: BuildArgs): AIEmpowerPayload {
    const { resources, shifts, planningWindow, siteBaseLocation, rsoConstraintLevels } = args;

    const realResources = (resources ?? []).filter((resource) => resource?.id && resource.id !== "0");
    const assignedShifts = (shifts ?? []).filter((shift) => shift?.resourceId && shift.resourceId !== "0");
    const unassignedShifts = (shifts ?? []).filter((shift) => !shift?.resourceId || shift.resourceId === "0");

    const mappedResources = logAIEmpowerMappedResources(realResources);
    const mappedBookings = logAIEmpowerBookings(assignedShifts);
    const mappedUnassigned = logAIEmpowerUnassignedJobs(unassignedShifts);

    return {
      resources: mappedResources.resources ?? [],
      bookings: mappedBookings.bookings ?? [],
      unassignedJobs: mappedUnassigned.unassignedJobs ?? [],
      planningWindow,
      siteBaseLocation,
      rsoConstraintLevels,
      explanation: {
        includeReasonPerAllocation: true,
        comparisonCriteria: [
          "hard feasibility: leave, unavailability, shift conflicts, and legal hour limits",
          "travel feasibility and route continuity",
          "skill compliance including expiry handling",
          "contract and preferred hours alignment",
          "cost minimization and pay exposure",
          "continuity of care",
          "fair distribution of workload"
        ],
        guidance:
          "For every suggested allocation, include a concise human-readable reason that cites the key comparisons leading to the suggestion. Reasons should reference feasibility, the active six optimisation goals, and the most important data points used for the decision."
      }
    };
  }
}

export default AIEmpowerPayloadBuilder;

export function buildAIEmpowerPayload(args: BuildArgs): AIEmpowerPayload {
  return AIEmpowerPayloadBuilder.build(args);
}
